import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  User, Trophy, Target, TrendingUp, Calendar, ArrowLeft, 
  Award, Zap, BarChart3, Medal
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function PlayerProfile({ user }) {
  const { playerName } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    if (playerName) {
      fetchProfile();
    }
  }, [playerName, selectedYear]);

  const fetchYears = async () => {
    try {
      const response = await axios.get(`${API}/standings/years`);
      setAvailableYears(response.data.years || [new Date().getFullYear()]);
    } catch (error) {
      console.error('Error fetching years:', error);
    }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/standings/player/${encodeURIComponent(playerName)}/profile?year=${selectedYear}`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to load player profile');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600 font-inter">Loading profile...</div>
        </div>
      </Layout>
    );
  }

  if (!profile) {
    return (
      <Layout user={user}>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="text-slate-600 font-inter">Player not found</div>
          <Button onClick={() => navigate('/standings')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Standings
          </Button>
        </div>
      </Layout>
    );
  }

  const { overview, division_breakdown, score_trend, monthly_progress, competition_history } = profile;

  return (
    <Layout user={user}>
      <div className="space-y-8">
        {/* Back Button */}
        <Button 
          onClick={() => navigate(-1)} 
          variant="ghost" 
          className="text-stone-600 hover:text-slate-900 -ml-2"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Back
        </Button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-3xl font-barlow font-bold text-slate-900 uppercase tracking-tight">
                {profile.player_name}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                {profile.is_registered ? (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                    Registered Member
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs font-medium rounded">
                    Unregistered
                  </span>
                )}
                <span className="text-stone-500 text-sm font-inter">
                  {selectedYear} Season
                </span>
              </div>
            </div>
          </div>

          {/* Year Selector */}
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-stone-300 rounded-sm font-inter text-sm bg-white focus:ring-2 focus:ring-emerald-600 focus:border-transparent"
            data-testid="year-selector"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year} Season</option>
            ))}
          </select>
        </div>

        {/* Stats Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={<Trophy className="w-5 h-5 text-amber-500" />}
            label="Total Points"
            value={overview.total_points}
            testId="total-points"
          />
          <StatCard
            icon={<Calendar className="w-5 h-5 text-blue-500" />}
            label="Competitions"
            value={overview.total_competitions}
            testId="total-competitions"
          />
          <StatCard
            icon={<Medal className="w-5 h-5 text-emerald-500" />}
            label="Wins"
            value={overview.wins}
            testId="wins"
          />
          <StatCard
            icon={<TrendingUp className="w-5 h-5 text-purple-500" />}
            label="Top 3 Rate"
            value={`${overview.win_rate}%`}
            testId="win-rate"
          />
        </div>

        {/* Best Achievements Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {overview.best_finish && (
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                <span className="font-barlow font-bold text-amber-800 uppercase text-sm tracking-wider">Best Finish</span>
              </div>
              <div className="text-2xl font-barlow font-bold text-amber-900">
                {getOrdinal(overview.best_finish.place)} Place
              </div>
              <div className="text-sm text-amber-700 font-inter mt-1">
                {overview.best_finish.division} • {formatDate(overview.best_finish.date)}
              </div>
            </div>
          )}

          {overview.highest_score && (
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                <span className="font-barlow font-bold text-emerald-800 uppercase text-sm tracking-wider">Highest Score</span>
              </div>
              <div className="text-2xl font-barlow font-bold text-emerald-900">
                {overview.highest_score.score}
              </div>
              <div className="text-sm text-emerald-700 font-inter mt-1">
                {overview.highest_score.division} • {formatDate(overview.highest_score.date)}
              </div>
            </div>
          )}

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-purple-600" strokeWidth={1.5} />
              <span className="font-barlow font-bold text-purple-800 uppercase text-sm tracking-wider">Top 3 Streak</span>
            </div>
            <div className="text-2xl font-barlow font-bold text-purple-900">
              {overview.longest_top_3_streak} {overview.longest_top_3_streak === 1 ? 'Competition' : 'Competitions'}
            </div>
            <div className="text-sm text-purple-700 font-inter mt-1">
              Consecutive top 3 finishes
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Score Trend Chart */}
          {score_trend.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-sm p-6">
              <h3 className="font-barlow font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                Score Trend
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={score_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11, fill: '#78716c' }}
                    tickFormatter={(val) => val ? val.slice(5) : ''}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#78716c' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e5e5',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                    formatter={(value, name) => [value, 'Score']}
                    labelFormatter={(label) => formatDate(label)}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="score" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Division Breakdown Pie Chart */}
          {division_breakdown.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-sm p-6">
              <h3 className="font-barlow font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                Points by Division
              </h3>
              <div className="flex items-center">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie
                      data={division_breakdown}
                      dataKey="points"
                      nameKey="division"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={false}
                    >
                      {division_breakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value} pts`, name]}
                      contentStyle={{ fontSize: '12px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {division_breakdown.map((div, index) => (
                    <div key={div.division} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-sm" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="font-inter text-slate-700">{div.division}</span>
                      </div>
                      <span className="font-mono text-slate-900 font-medium">{div.points} pts</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Monthly Progress */}
        {monthly_progress.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-sm p-6">
            <h3 className="font-barlow font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" strokeWidth={1.5} />
              Monthly Progress
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthly_progress}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 11, fill: '#78716c' }}
                  tickFormatter={(val) => {
                    const [year, month] = val.split('-');
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    return monthNames[parseInt(month) - 1] || val;
                  }}
                />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#78716c' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#78716c' }} />
                <Tooltip 
                  contentStyle={{ fontSize: '12px' }}
                  formatter={(value, name) => {
                    if (name === 'avg_score') return [value, 'Avg Score'];
                    if (name === 'total_points') return [value, 'Points'];
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="avg_score" name="Avg Score" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="total_points" name="Points" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Competition History Table */}
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-200 bg-stone-50">
            <h3 className="font-barlow font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
              Competition History
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="competition-history-table">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="py-3 px-4 text-left font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Date</th>
                  <th className="py-3 px-4 text-left font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Division</th>
                  <th className="py-3 px-4 text-center font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Place</th>
                  <th className="py-3 px-4 text-center font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Score</th>
                  <th className="py-3 px-4 text-center font-barlow font-bold text-xs uppercase tracking-wider text-stone-600">Points</th>
                </tr>
              </thead>
              <tbody>
                {competition_history.map((comp, index) => (
                  <tr key={`${comp.date}-${comp.division}-${comp.score}-${index}`} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-3 px-4 font-inter text-sm text-slate-900">
                      {formatDate(comp.date)}
                    </td>
                    <td className="py-3 px-4 font-inter text-sm text-slate-700">
                      {comp.division}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {comp.place ? (
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          comp.place === 1 ? 'bg-amber-100 text-amber-700' :
                          comp.place === 2 ? 'bg-stone-200 text-stone-700' :
                          comp.place === 3 ? 'bg-orange-100 text-orange-700' :
                          'bg-stone-100 text-stone-600'
                        }`}>
                          {comp.place}
                        </span>
                      ) : (
                        <span className="text-stone-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center font-mono text-sm text-slate-900">
                      {comp.score}
                    </td>
                    <td className="py-3 px-4 text-center font-mono text-sm font-bold text-emerald-600">
                      {comp.points > 0 ? `+${comp.points}` : '-'}
                    </td>
                  </tr>
                ))}
                {competition_history.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-stone-500 font-inter">
                      No competition history found for {selectedYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div className="bg-white border border-stone-200 rounded-sm p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="font-inter text-xs text-stone-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-barlow font-bold text-slate-900">{value}</div>
    </div>
  );
}
